/**
 * Normalizes date fields to the format the API requires (EU-402).
 *
 * The API validates date fields as ISO 8601 (`YYYY-MM-DDTHH:mm:ss.sssZ`) and
 * rejects any other format with 400 `FST_ERR_VALIDATION`. The `DateTimePicker`
 * emits ISO on the field the user touched, but values hydrated from an API read
 * arrive as a raw timestamp (`"2026-06-01 03:00:00+00"`) and, if sent back
 * verbatim, break the mutation. Centralizing the normalization here (RN-02)
 * guarantees no mutation sends a date outside ISO 8601 (RN-01/RN-03), on the
 * frontend side (RN-04), regardless of whether the container normalized the
 * value.
 */

/**
 * Canonicalizes a date value into an ISO 8601 instant, or `null`.
 *
 * Accepts the ISO from the `DateTimePicker` and the raw timestamp from an API
 * read — `new Date(...).toISOString()` converts both identically. `null`/
 * `undefined`/`''` (the "no date" sentinel) become `null`; an unparseable value
 * also becomes `null` instead of forwarding garbage to the API (the form schema
 * already rejects invalid dates before this point).
 *
 * `Date.parse` of the raw Postgres-style timestamp (`"2026-06-01 03:00:00+00"`)
 * is implementation-defined per the ECMAScript spec, but V8 (browser) and JSC
 * (test runtime) parse it to the same instant — verified across the formats this
 * helper sees. If a runtime ever diverges, switch to a deterministic regex parse
 * of the Postgres format.
 */
export function toApiInstant(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  if (typeof value !== 'string') return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

/**
 * Instant date-field keys that travel to the API in the recruiter mutations
 * (programs + jobs). Every key here is normalized by `withNormalizedApiDates`
 * before the request is sent.
 *
 * Only fields whose contract is a UTC instant belong here. The `startDate`/
 * `endDate` of the step modules in `PUT /jobs/:id/steps` are **date-only** by
 * contract (`^\d{4}-\d{2}-\d{2}$`) and live nested inside arrays — which is why
 * the normalization is deliberately **top-level only**, never a deep walk:
 * converting those to an instant would break the steps endpoint.
 *
 * `publishedAt` is kept for completeness with RN-01 (*all* date fields in ISO
 * 8601): `ProgramCreateBasePayload` declares it and the create flow normalizes
 * the base payload, so a caller that hydrates `publishedAt` from a read stays
 * covered. No emitter populates it from a hydrated value today — it's
 * defensive, not dead.
 */
const API_INSTANT_KEYS = [
  'startApplying',
  'endApplying',
  'startDate',
  'endDate',
  'publishedAt',
] as const;

/**
 * Returns a shallow copy of `payload` with every known (top-level) instant date
 * field normalized to ISO 8601. Missing keys are ignored; present keys with an
 * empty value become `null`. No other field is touched, and nested objects/
 * arrays stay intact. When nothing changes, `payload` itself is returned (no
 * copy).
 */
export function withNormalizedApiDates<T>(payload: T): T {
  if (payload == null || typeof payload !== 'object') return payload;
  const source = payload as Record<string, unknown>;
  let next: Record<string, unknown> | null = null;
  for (const key of API_INSTANT_KEYS) {
    if (!(key in source)) continue;
    const normalized = toApiInstant(source[key]);
    if (normalized !== source[key]) {
      next ??= { ...source };
      next[key] = normalized;
    }
  }
  return (next ?? payload) as T;
}
